import { useEffect, useMemo, useState } from 'react'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { Card } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip
} from '@mui/material'
import { toast } from 'react-toastify'
import { Braces, Eye, HelpCircle, Radio, Wrench } from 'lucide-react'

const ACTIVE_FILTERS = [
  { value: 'all', label: 'Tutti' },
  { value: '1', label: 'Attivi' },
  { value: '0', label: 'Disattivi' }
]

const DEPRECATED_FILTERS = [
  { value: 'all', label: 'Tutti' },
  { value: '0', label: 'Non deprecati' },
  { value: '1', label: 'Deprecati' }
]

function formatDateIt(value) {
  if (!value) return ''
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return ''
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return trimmed
    const iso = trimmed.includes('T') ? trimmed.slice(0, 10) : trimmed
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [y, m, d] = iso.split('-')
      return `${d}/${m}/${y}`
    }
  }
  try {
    const d = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = String(d.getFullYear())
    return `${dd}/${mm}/${yyyy}`
  } catch {
    return ''
  }
}

function parseDateToIso(value) {
  if (!value) return null
  const trimmed = String(value).trim()
  if (!trimmed) return null
  if (trimmed.includes('T') && /^\d{4}-\d{2}-\d{2}T/.test(trimmed)) return trimmed.slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
  const m = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (m) {
    const dd = Number(m[1])
    const mm = Number(m[2])
    const yyyy = Number(m[3])
    if (dd >= 1 && dd <= 31 && mm >= 1 && mm <= 12 && yyyy >= 1900 && yyyy <= 2100) {
      return `${String(yyyy).padStart(4, '0')}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
    }
  }
  return null
}

function CapabilityChip({ enabled, label, helpText, Icon }) {
  return (
    <Tooltip title={helpText || label} placement="top" arrow>
      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
        <Icon className={`h-3.5 w-3.5 ${enabled ? 'text-primary' : 'text-muted-foreground/50'}`} />
        <span className={enabled ? 'text-muted-foreground' : 'text-muted-foreground/50'}>{label}</span>
      </span>
    </Tooltip>
  )
}

function CapabilitySwitchLabel({ label, helpText, Icon }) {
  return (
    <span className="inline-flex items-center gap-1">
      <Icon className="h-4 w-4" />
      <span>{label}</span>
      <Tooltip title={helpText || label} placement="top" arrow>
        <span className="inline-flex items-center">
          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
        </span>
      </Tooltip>
    </span>
  )
}

function AiLlmModelsAdmin() {
  const { user } = useAuth()
  const roleName = (user?.role_name || user?.role || '').toLowerCase()
  const isAdmin = roleName === 'admin'

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)

  const [search, setSearch] = useState('')
  const [fornitore, setFornitore] = useState('')
  const [fornitoriOptions, setFornitoriOptions] = useState([])
  const [attivo, setAttivo] = useState('all')
  const [deprecato, setDeprecato] = useState('all')

  const [page, setPage] = useState(1)
  const limit = 50

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [testingId, setTestingId] = useState(null)
  const [testingBatch, setTestingBatch] = useState(false)
  const [syncing, setSyncing] = useState(false)

  const isNew = !!editing && !editing.id

  const queryParams = useMemo(() => {
    const params = {
      page,
      limit,
      orderBy: 'fornitore',
      orderDirection: 'asc'
    }
    if (search.trim()) params.search = search.trim()
    if (fornitore.trim()) params.fornitore = fornitore.trim()
    if (attivo !== 'all') params.attivo = attivo
    if (deprecato !== 'all') params.deprecato = deprecato
    return params
  }, [search, fornitore, attivo, deprecato, page])

  const load = async () => {
    try {
      setLoading(true)
      const res = await api.get('/ai/modelli-llm', { params: queryParams })
      setRows(res.data?.data || [])
      setTotal(Number(res.data?.total || 0))
    } catch (e) {
      toast.error('Errore nel caricamento dei modelli LLM')
    } finally {
      setLoading(false)
    }
  }

  const loadFornitori = async () => {
    try {
      const res = await api.get('/ai/modelli-llm/fornitori')
      const items = Array.isArray(res.data?.data) ? res.data.data : []
      setFornitoriOptions(items.filter(Boolean))
    } catch (_) {
      setFornitoriOptions([])
    }
  }

  useEffect(() => {
    if (!isAdmin) return
    load()
  }, [isAdmin, queryParams])

  useEffect(() => {
    if (!isAdmin) return
    loadFornitori()
  }, [isAdmin])

  const startNew = () => {
    setEditing({
      fornitore: '',
      nome_api: '',
      nome_visualizzazione: '',
      versione: '',
      famiglia: '',
      note_utilizzo: '',
      finestra_contesto: '',
      token_output_max: '',
      supporta_visione: false,
      supporta_strumenti: false,
      supporta_streaming: false,
      supporta_json: true,
      prezzo_input_mtok: '',
      prezzo_output_mtok: '',
      attivo: true,
      deprecato: false,
      deprecato_il: '',
      rilasciato_il: ''
    })
    setDialogOpen(true)
  }

  const startEdit = (r) => {
    setEditing({
      id: r.id,
      fornitore: r.fornitore || '',
      nome_api: r.nome_api || '',
      nome_visualizzazione: r.nome_visualizzazione || '',
      versione: r.versione || '',
      famiglia: r.famiglia || '',
      note_utilizzo: r.note_utilizzo || '',
      finestra_contesto: r.finestra_contesto ?? '',
      token_output_max: r.token_output_max ?? '',
      supporta_visione: r.supporta_visione === 1 || r.supporta_visione === true,
      supporta_strumenti: r.supporta_strumenti === 1 || r.supporta_strumenti === true,
      supporta_streaming: r.supporta_streaming === 1 || r.supporta_streaming === true,
      supporta_json: r.supporta_json === 1 || r.supporta_json === true,
      prezzo_input_mtok: r.prezzo_input_mtok ?? '',
      prezzo_output_mtok: r.prezzo_output_mtok ?? '',
      attivo: r.attivo === 1 || r.attivo === true,
      deprecato: r.deprecato === 1 || r.deprecato === true,
      deprecato_il: formatDateIt(r.deprecato_il),
      rilasciato_il: formatDateIt(r.rilasciato_il)
    })
    setDialogOpen(true)
  }

  const cancelEdit = () => {
    setEditing(null)
    setDialogOpen(false)
  }

  const handleChange = (field, value) => {
    setEditing((prev) => ({ ...(prev || {}), [field]: value }))
  }

  const handleSave = async () => {
    if (!editing) return
    try {
      setSaving(true)
      const deprecatoIso = parseDateToIso(editing.deprecato_il)
      const rilasciatoIso = parseDateToIso(editing.rilasciato_il)
      if (editing.deprecato_il && !deprecatoIso) {
        toast.error('Formato data non valido per "Deprecato il". Usa GG/MM/AAAA.')
        return
      }
      if (editing.rilasciato_il && !rilasciatoIso) {
        toast.error('Formato data non valido per "Rilasciato il". Usa GG/MM/AAAA.')
        return
      }
      const payload = {
        ...editing,
        deprecato_il: deprecatoIso,
        rilasciato_il: rilasciatoIso
      }
      const res = await api.post('/ai/modelli-llm', payload)
      const saved = res.data
      setDialogOpen(false)
      setEditing(null)
      setRows((prev) => {
        const others = prev.filter((x) => x.id !== saved.id)
        return [saved, ...others]
      })
      toast.success('Modello salvato')
      loadFornitori()
      load()
    } catch (e) {
      const msg = e?.response?.data?.message || 'Errore nel salvataggio del modello'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (r) => {
    try {
      const res = await api.patch(`/ai/modelli-llm/${r.id}/active`, { attivo: !(r.attivo === 1 || r.attivo === true) })
      const saved = res.data
      setRows((prev) => prev.map((x) => (x.id === saved.id ? saved : x)))
    } catch (e) {
      toast.error('Errore aggiornando lo stato attivo')
    }
  }

  const handleToggleDeprecated = async (r) => {
    try {
      const next = !(r.deprecato === 1 || r.deprecato === true)
      const res = await api.patch(`/ai/modelli-llm/${r.id}/deprecate`, {
        deprecato: next,
        deprecato_il: next ? new Date().toISOString().slice(0, 10) : null
      })
      const saved = res.data
      setRows((prev) => prev.map((x) => (x.id === saved.id ? saved : x)))
    } catch (e) {
      toast.error('Errore aggiornando lo stato deprecato')
    }
  }

  const handleTestModel = async (r) => {
    try {
      setTestingId(r.id)
      const res = await api.post(`/ai/modelli-llm/${r.id}/test`, { activateOnSuccess: true })
      const data = res.data
      if (data && data.ok) {
        if (data.activated) {
          toast.success('Test OK: modello attivato')
        } else {
          toast.success('Test OK')
        }
        if (data.row) {
          setRows((prev) => prev.map((x) => (x.id === data.row.id ? data.row : x)))
        } else {
          load()
        }
        return
      }
      toast.error(data?.error || 'Test fallito')
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Test fallito'
      toast.error(msg)
    } finally {
      setTestingId(null)
    }
  }

  const handleTestCurrentPage = async () => {
    const ids = rows.map((r) => r.id)
    if (!ids.length) return
    try {
      setTestingBatch(true)
      const res = await api.post('/ai/modelli-llm/test-batch', { ids, activateOnSuccess: true, max: 50 })
      const payload = res.data
      const okCount = Number(payload?.okCount || 0)
      const activatedCount = Number(payload?.activatedCount || 0)
      const totalCount = Number(payload?.total || ids.length)
      toast.success(`Test completati: ${okCount}/${totalCount} OK • Attivati: ${activatedCount}`)
      const updatedRows = Array.isArray(payload?.results)
        ? payload.results.map((r) => r.row).filter(Boolean)
        : []
      if (updatedRows.length) {
        setRows((prev) => prev.map((x) => updatedRows.find((u) => u.id === x.id) || x))
      } else {
        load()
      }
    } catch (e) {
      toast.error('Errore durante il test dei modelli')
    } finally {
      setTestingBatch(false)
    }
  }

  const handleSyncFromProviders = async () => {
    try {
      setSyncing(true)
      const res = await api.post('/ai/modelli-llm/sync', {})
      const data = res.data || {}
      const inserted = Number(data.inserted || 0)
      const updated = Number(data.updated || 0)
      const totalSync = Number(data.total || 0)
      toast.success(`Sync completata: ${inserted} nuovi • ${updated} aggiornati • ${totalSync} letti`)
      const providers = data.providers || {}
      Object.keys(providers).forEach((k) => {
        const p = providers[k]
        if (p && p.ok === false) {
          toast.error(`${k}: ${p.error || 'Errore'}`)
        }
      })
      loadFornitori()
      load()
    } catch (e) {
      toast.error('Errore durante la sincronizzazione dei modelli')
    } finally {
      setSyncing(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit))

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold mb-2">AI – Modelli LLM</h1>
        <p className="text-sm text-muted-foreground">Accesso riservato agli amministratori.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">AI – Modelli LLM</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Catalogo modelli (capacitá, prezzi, stato) per standardizzare le scelte in AI Settings e nei flussi applicativi.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleTestCurrentPage} disabled={testingBatch || loading || !rows.length}>
            {testingBatch ? 'Test in corso…' : 'Test pagina'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleSyncFromProviders} disabled={syncing || loading}>
            {syncing ? 'Sync modelli…' : 'Recupera modelli'}
          </Button>
          <Button size="sm" onClick={startNew}>
            Nuovo modello
          </Button>
        </div>
      </div>

      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">Cerca</span>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome, API, famiglia..." className="w-64" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">Fornitore</span>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="llm-fornitore-filter">Fornitore</InputLabel>
              <Select
                labelId="llm-fornitore-filter"
                label="Fornitore"
                value={fornitore}
                onChange={(e) => {
                  setFornitore(e.target.value)
                  setPage(1)
                }}
              >
                <MenuItem value="">
                  <em>Tutti</em>
                </MenuItem>
                {fornitoriOptions.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">Stato</span>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="llm-attivo-filter">Attivo</InputLabel>
              <Select labelId="llm-attivo-filter" label="Attivo" value={attivo} onChange={(e) => setAttivo(e.target.value)}>
                {ACTIVE_FILTERS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">Deprecato</span>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="llm-deprecato-filter">Deprecato</InputLabel>
              <Select labelId="llm-deprecato-filter" label="Deprecato" value={deprecato} onChange={(e) => setDeprecato(e.target.value)}>
                {DEPRECATED_FILTERS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPage(1)
              load()
            }}
            disabled={loading}
          >
            Aggiorna
          </Button>
        </div>
        <div className="text-[11px] text-muted-foreground">
          Nota: molti modelli vengono catalogati ma non sono “attivi” di default. Per vederli, imposta <span className="font-medium">Stato</span> su <span className="font-medium">Tutti</span>.
        </div>
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span>Totale: {total}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </Button>
            <span>
              Pagina {page} / {totalPages}
            </span>
            <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
              Next
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-4 space-y-2">
        <div className="space-y-1">
          {rows.map((r) => (
            <div
              key={r.id}
              className="grid grid-cols-1 md:grid-cols-[minmax(320px,1fr)_minmax(280px,420px)_auto] items-start gap-3 border-b last:border-b-0 py-3"
            >
              <div className="space-y-1 min-w-0">
                <div className="text-sm font-medium">
                  {r.nome_visualizzazione}{' '}
                  <span className="text-xs text-muted-foreground">
                    ({r.fornitore} • {r.nome_api})
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {r.famiglia ? `${r.famiglia}` : '—'}
                  {r.versione ? ` • v${r.versione}` : ''}
                  {r.finestra_contesto ? ` • ctx ${r.finestra_contesto}` : ''}
                  {r.token_output_max ? ` • out ${r.token_output_max}` : ''}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant="outline"
                    className={`text-[9px] ${
                      r.attivo
                        ? 'border-green-200 bg-green-50 text-green-700'
                        : 'border-red-200 bg-red-50 text-red-700'
                    }`}
                  >
                    {r.attivo ? 'Attivo' : 'Disattivo'}
                  </Badge>
                  {r.deprecato ? (
                    <Badge variant="secondary" className="text-[9px]">
                      Deprecato{r.deprecato_il ? ` (${formatDateIt(r.deprecato_il)})` : ''}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[9px]">
                      Non deprecato
                    </Badge>
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    <CapabilityChip
                      enabled={r.supporta_visione === 1 || r.supporta_visione === true}
                      label="Visione"
                      helpText="Supporta input con immagini."
                      Icon={Eye}
                    />
                    <CapabilityChip
                      enabled={r.supporta_strumenti === 1 || r.supporta_strumenti === true}
                      label="Strumenti"
                      helpText="Supporta tool/function calling."
                      Icon={Wrench}
                    />
                    <CapabilityChip
                      enabled={r.supporta_streaming === 1 || r.supporta_streaming === true}
                      label="Streaming"
                      helpText="Supporta output in streaming."
                      Icon={Radio}
                    />
                    <CapabilityChip
                      enabled={r.supporta_json === 1 || r.supporta_json === true}
                      label="JSON"
                      helpText="Supporta output strutturato (JSON)."
                      Icon={Braces}
                    />
                  </div>
                </div>
              </div>

              <div className="min-w-0">
                <div className="text-[11px] font-medium text-muted-foreground">Note</div>
                {r.note_utilizzo ? (
                  <Tooltip title={String(r.note_utilizzo)} placement="top" arrow>
                    <div className="text-[11px] text-muted-foreground whitespace-pre-wrap break-words">
                      {String(r.note_utilizzo).length > 320 ? `${String(r.note_utilizzo).slice(0, 320)}…` : String(r.note_utilizzo)}
                    </div>
                  </Tooltip>
                ) : (
                  <div className="text-[11px] text-muted-foreground/60">—</div>
                )}
              </div>

              <div className="flex items-center gap-2 justify-start md:justify-end">
                <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={() => startEdit(r)}>
                  Modifica
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => handleTestModel(r)}
                  disabled={testingId === r.id}
                >
                  {testingId === r.id ? 'Test…' : 'Test'}
                </Button>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={() => handleToggleActive(r)}>
                  {r.attivo ? 'Disattiva' : 'Attiva'}
                </Button>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={() => handleToggleDeprecated(r)}>
                  {r.deprecato ? 'Ripristina' : 'Depreca'}
                </Button>
              </div>
            </div>
          ))}
          {!rows.length && (
            <div className="text-[11px] text-muted-foreground px-1 py-2">
              Nessun modello trovato con i filtri correnti.
            </div>
          )}
        </div>
      </Card>

      <Dialog open={dialogOpen && !!editing} onClose={cancelEdit} maxWidth="md" fullWidth>
        <DialogTitle>{isNew ? 'Nuovo modello LLM' : `Modifica modello #${editing?.id}`}</DialogTitle>
        {editing && (
          <DialogContent dividers>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Fornitore</span>
                <Input value={editing.fornitore} onChange={(e) => handleChange('fornitore', e.target.value)} placeholder="Es. OpenAI" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Nome API</span>
                <Input value={editing.nome_api} onChange={(e) => handleChange('nome_api', e.target.value)} placeholder="Es. gpt-4o-mini" />
              </div>
              <div className="flex flex-col gap-1 md:col-span-2">
                <span className="text-xs font-medium text-muted-foreground">Nome visualizzazione</span>
                <Input value={editing.nome_visualizzazione} onChange={(e) => handleChange('nome_visualizzazione', e.target.value)} placeholder="Es. GPT-4o mini" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Versione</span>
                <Input value={editing.versione} onChange={(e) => handleChange('versione', e.target.value)} placeholder="Es. 4o" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Famiglia</span>
                <Input value={editing.famiglia} onChange={(e) => handleChange('famiglia', e.target.value)} placeholder="Es. GPT-4" />
              </div>
              <div className="flex flex-col gap-1 md:col-span-2">
                <span className="text-xs font-medium text-muted-foreground">Nota utilizzo</span>
                <TextField
                  value={editing.note_utilizzo}
                  onChange={(e) => handleChange('note_utilizzo', e.target.value)}
                  placeholder="Quando consigliarlo, pro/contro, casi d’uso tipici…"
                  multiline
                  minRows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Finestra contesto (token)</span>
                <Input value={editing.finestra_contesto} onChange={(e) => handleChange('finestra_contesto', e.target.value)} type="number" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Token output max</span>
                <Input value={editing.token_output_max} onChange={(e) => handleChange('token_output_max', e.target.value)} type="number" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Prezzo input / M token (USD)</span>
                <Input value={editing.prezzo_input_mtok} onChange={(e) => handleChange('prezzo_input_mtok', e.target.value)} type="number" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Prezzo output / M token (USD)</span>
                <Input value={editing.prezzo_output_mtok} onChange={(e) => handleChange('prezzo_output_mtok', e.target.value)} type="number" />
              </div>
              <div className="flex flex-col gap-2 md:col-span-2">
                <div className="flex flex-wrap gap-3">
                  <FormControlLabel
                    control={<Switch checked={!!editing.supporta_visione} onChange={(e) => handleChange('supporta_visione', e.target.checked)} />}
                    label={<CapabilitySwitchLabel label="Supporta visione" helpText="Abilita input con immagini." Icon={Eye} />}
                  />
                  <FormControlLabel
                    control={<Switch checked={!!editing.supporta_strumenti} onChange={(e) => handleChange('supporta_strumenti', e.target.checked)} />}
                    label={<CapabilitySwitchLabel label="Supporta strumenti" helpText="Abilita tool/function calling." Icon={Wrench} />}
                  />
                  <FormControlLabel
                    control={<Switch checked={!!editing.supporta_streaming} onChange={(e) => handleChange('supporta_streaming', e.target.checked)} />}
                    label={<CapabilitySwitchLabel label="Supporta streaming" helpText="Abilita output in streaming." Icon={Radio} />}
                  />
                  <FormControlLabel
                    control={<Switch checked={!!editing.supporta_json} onChange={(e) => handleChange('supporta_json', e.target.checked)} />}
                    label={<CapabilitySwitchLabel label="Supporta JSON" helpText="Favorisce output JSON valido (dipende dal provider)." Icon={Braces} />}
                  />
                </div>
                <div className="flex flex-wrap gap-3">
                  <FormControlLabel
                    control={<Switch checked={!!editing.attivo} onChange={(e) => handleChange('attivo', e.target.checked)} />}
                    label="Attivo"
                  />
                  <FormControlLabel
                    control={<Switch checked={!!editing.deprecato} onChange={(e) => handleChange('deprecato', e.target.checked)} />}
                    label="Deprecato"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Deprecato il (GG/MM/AAAA)</span>
                <Input value={editing.deprecato_il} onChange={(e) => handleChange('deprecato_il', e.target.value)} placeholder="28/05/2026" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Rilasciato il (GG/MM/AAAA)</span>
                <Input value={editing.rilasciato_il} onChange={(e) => handleChange('rilasciato_il', e.target.value)} placeholder="28/05/2026" />
              </div>
            </div>
          </DialogContent>
        )}
        <DialogActions>
          <Button variant="ghost" onClick={cancelEdit}>
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Salvataggio…' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default AiLlmModelsAdmin
